import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Sparkles, Copy } from 'lucide-react';
import { Card, CardContent } from '@ecom/ui/card';
import { Button } from '@ecom/ui/button';
import { Input } from '@ecom/ui/input';
import { Label } from '@ecom/ui/label';
import { Textarea } from '@ecom/ui/textarea';

export const Route = createFileRoute('/(app)/ai/listing-studio')({
  component: ListingStudioPage,
});

/**
 * Phase 3 — wire to trpc.ai.generateListing.
 * For now, demonstrates the UX: input → AI output → copy.
 */
function ListingStudioPage() {
  const [titleHint, setTitleHint] = useState('');
  const [features, setFeatures] = useState('');
  const [targetLang, setTargetLang] = useState<'en' | 'de' | 'ja'>('en');
  const [result, setResult] = useState<null | { title: string; bullets: string[] }>(null);

  function handleGenerate() {
    // Phase 3: trpc.ai.generateListing.mutate(...)
    setResult({
      title: `[Demo] ${titleHint} — Premium Quality`,
      bullets: features.split('\n').filter(Boolean).map((f) => `• ${f}`),
    });
  }

  return (
    <div className="p-6 grid md:grid-cols-2 gap-6">
      <Card className="p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-violet-500" />
          <h2 className="text-lg font-semibold">AI Listing Studio</h2>
        </div>

        <div>
          <Label>Product / keyword</Label>
          <Input value={titleHint}
            onChange={(e) => setTitleHint(e.target.value)}
            placeholder="Stainless steel insulated water bottle 500ml" />
        </div>

        <div>
          <Label>Key selling points (one per line)</Label>
          <Textarea rows={6} value={features}
            onChange={(e) => setFeatures(e.target.value)}
            placeholder={"316 stainless steel interior\n12h insulation\nLeak-proof design\nBPA-free"} />
        </div>

        <div>
          <Label>Target language</Label>
          <select value={targetLang}
            onChange={(e) => setTargetLang(e.target.value as 'en' | 'de' | 'ja')}
            className="w-full border rounded px-3 py-2 text-sm">
            <option value="en">English</option>
            <option value="de">Deutsch</option>
            <option value="ja">日本語</option>
          </select>
        </div>

        <Button className="w-full" onClick={handleGenerate} disabled={!titleHint || !features}>
          <Sparkles className="h-4 w-4 mr-2" /> Generate Listing
        </Button>
      </Card>

      <Card className="p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Generated Listing</h3>
          {result && (
            <Button size="sm" variant="ghost" onClick={() => navigator.clipboard.writeText(JSON.stringify(result, null, 2))}>
              <Copy className="h-3 w-3 mr-1" /> Copy
            </Button>
          )}
        </div>
        {!result && (
          <CardContent className="text-center text-muted-foreground py-12">
            Fill in the form and click Generate.
          </CardContent>
        )}
        {result && (
          <div className="space-y-3 text-sm">
            <div className="p-3 bg-muted rounded">
              <div className="text-xs text-muted-foreground mb-1">Title</div>
              <div className="font-medium">{result.title}</div>
            </div>
            <div className="p-3 bg-muted rounded">
              <div className="text-xs text-muted-foreground mb-1">Bullets</div>
              <ul className="space-y-1">
                {result.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
